import { openSseStream, readSseStream } from '../../hooks/sseStreamCore';
import type { StrategySimulationRunResponse } from '../../types';

/** Matches backend `ARM_MS_DEFAULT` (strategySimulationService.ts). */
export const STRATEGY_SIMULATION_SERVER_ARM_TIMEOUT_MS = 35_000;

/**
 * Extra poll budget for hybrid LLM miss processing (same-prompt cache + full LLM for misses).
 * Aligns client wait with longer server-side evaluation when hybrid is enabled.
 */
export const STRATEGY_SIMULATION_HYBRID_LLM_BUFFER_MS = 120_000;

/**
 * Shared arms run sequential timed segments (embedding merge, default_tiered, default_legacy).
 * Overall client-enforced deadline covering worst-case wall time before the run can reach
 * `completed`, plus hybrid LLM work. If no terminal status arrives before this elapses, the
 * stream is aborted and the poll rejects with `StrategySimulationPollTimeout`.
 */
export const STRATEGY_SIMULATION_POLL_MAX_WAIT_MS =
    3 * STRATEGY_SIMULATION_SERVER_ARM_TIMEOUT_MS + 10_000 + STRATEGY_SIMULATION_HYBRID_LLM_BUFFER_MS;

const TERMINAL_RUN_STATUS = new Set(['completed', 'failed']);

export class StrategySimulationPollTimeout extends Error {
    constructor() {
        super('Strategy simulation poll timed out');
        this.name = 'StrategySimulationPollTimeout';
    }
}

export class StrategySimulationGatewayTimeout extends Error {
    constructor() {
        super('Gateway timeout');
        this.name = 'StrategySimulationGatewayTimeout';
    }
}

function streamUrl(promptId: string, runId: string): string {
    const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    return `${base}/api/prompts/${promptId}/strategy-simulations/${runId}/stream`;
}

/**
 * Subscribes to the run's SSE stream (strategySimulationController.streamStrategySimulation)
 * and resolves with the terminal snapshot once status is completed/failed. Replaces the old
 * exponential-backoff REST poll; reuses the same fetch + SSE-parsing mechanics as the shared
 * `useEventStream` hook (via `hooks/sseStreamCore`), but as a one-shot Promise since callers
 * here are imperative (button-triggered), not render-driven.
 *
 * Enforces `maxWaitMs` (default `STRATEGY_SIMULATION_POLL_MAX_WAIT_MS`) as an overall deadline:
 * if no terminal status arrives in time, the stream is aborted and the promise rejects with
 * `StrategySimulationPollTimeout`, racing the read loop rather than relying on the mocked-away
 * fetch to honor the abort signal.
 */
export async function subscribeStrategySimulationRun(
    promptId: string,
    runId: string,
    options: {
        maxWaitMs?: number;
        onPoll?: (data: StrategySimulationRunResponse) => void;
    }
): Promise<StrategySimulationRunResponse> {
    const maxWaitMs = options.maxWaitMs ?? STRATEGY_SIMULATION_POLL_MAX_WAIT_MS;
    const controller = new AbortController();

    let timeoutId: ReturnType<typeof setTimeout>;
    const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
            controller.abort();
            reject(new StrategySimulationPollTimeout());
        }, maxWaitMs);
    });

    const runPromise = (async (): Promise<StrategySimulationRunResponse> => {
        const response = await openSseStream(streamUrl(promptId, runId), {
            signal: controller.signal,
        });

        if (response.status === 504) {
            throw new StrategySimulationGatewayTimeout();
        }
        if (!response.ok || !response.body) {
            throw new Error(`HTTP ${response.status}`);
        }

        let result: StrategySimulationRunResponse | undefined;
        await readSseStream(response, ({ event, data }) => {
            if (event !== 'update') return;
            const run = data as StrategySimulationRunResponse;
            if (TERMINAL_RUN_STATUS.has(run.status)) {
                result = run;
                return true;
            }
            options.onPoll?.(run);
        });

        if (!result) {
            throw new Error('Strategy simulation stream ended unexpectedly');
        }
        return result;
    })();

    try {
        return await Promise.race([runPromise, timeoutPromise]);
    } finally {
        clearTimeout(timeoutId!);
    }
}
